'use client';

/**
 * SectorLeaders — six cards (one per peer-group flavor), top 3 stocks each
 * by composite score, Q ≥ 0 gate preserved. Read-only view; not a basket,
 * not rebalanced.
 *
 * Lives on /alpha-zone?tab=leaders. Companion to the main Alpha Basket,
 * which is global-top-10. This cuts by peer-group so the eye doesn't get
 * monopolized by whatever sector dominates the global stack.
 */
import { useState } from 'react';
import { useResource } from '@/lib/use-resource';
import { compositeTier } from '@/lib/composite-tiers';
import { StockStoryDrawer } from '@/components/StockStoryDrawer';

type PeerGroup = 'standard' | 'financial' | 'insurance' | 'energy' | 'utility' | 'reit' | 'materials';

interface Leader {
  symbol: string;
  name: string;
  sector: string;
  industry: string;
  q: number;
  v: number;
  c: number;
  price: number;
  rank: number;
}

interface Payload {
  asOf: string | null;
  topN: number;
  order: PeerGroup[];
  labels: Record<PeerGroup, string>;
  leaders: Record<PeerGroup, Leader[]>;
}

const PEER_GROUP_BLURB: Record<PeerGroup, string> = {
  standard:  'Operating businesses scored on Profit / Growth / Safety / Payout with peer-relative + absolute valuation.',
  financial: 'Banks scored with bank-flavored Quality: Tier-1 capital, NPL, ROTCE, NIM. Brokers / asset managers excluded.',
  insurance: 'P&C carriers scored with combined ratio, loss ratio, expense ratio, investment income — separate from banks.',
  energy:    'Oil, gas, and pipeline names. Commodity-linked margins; book value matters more than P/E.',
  utility:   'Regulated and independent power producers. Stable cash flows, dividend-heavy.',
  reit:      'REITs scored on FFO yield, NAV, P/FFO, occupancy, same-store NOI growth. P/E framework retired here.',
  materials: 'Basic Materials (gold, mining, chemicals) cut out of Standard so the best commodity name on the board isn\'t crowded out by mega-cap tech.',
};

function tierColor(c: number): string {
  return compositeTier(c).hex;
}

function LeaderRow({ row, onClick }: { row: Leader; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full grid grid-cols-[24px_1fr_56px_56px_56px] gap-2 items-baseline text-left py-1.5 px-2 rounded hover:bg-white/[0.03] border border-transparent hover:border-white/[0.06] transition-colors"
    >
      <div className="text-[11px] tabular-nums text-zinc-500">#{row.rank}</div>
      <div className="min-w-0">
        <div className="text-[13px] font-bold text-white truncate">{row.symbol}</div>
        <div className="text-[10px] text-zinc-400 truncate">{row.name}</div>
      </div>
      <div className="text-right text-[11px] tabular-nums" style={{ color: row.q >= 0 ? '#4ade80' : '#f87171' }}>
        Q {row.q.toFixed(2)}
      </div>
      <div className="text-right text-[11px] tabular-nums" style={{ color: row.v >= 0 ? '#4ade80' : '#f87171' }}>
        V {row.v.toFixed(2)}
      </div>
      <div className="text-right text-[11px] font-bold tabular-nums" style={{ color: tierColor(row.c) }}>
        C {row.c.toFixed(2)}
      </div>
    </button>
  );
}

function SectorCard({
  label,
  blurb,
  rows,
  onPickSymbol,
}: {
  label: string;
  blurb: string;
  rows: Leader[];
  onPickSymbol: (symbol: string) => void;
}) {
  return (
    <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-4">
      <div className="mb-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300/80 font-semibold">{label}</div>
        <div className="text-[10px] text-zinc-500 leading-snug mt-0.5">{blurb}</div>
      </div>
      <div className="mt-2 border-t border-white/[0.04] pt-1.5">
        {rows.length === 0 ? (
          <div className="text-[11px] text-zinc-500 italic py-3 text-center">
            No qualifiers today — every name in this group has Q &lt; 0.
          </div>
        ) : (
          rows.map((row) => (
            <LeaderRow key={row.symbol} row={row} onClick={() => onPickSymbol(row.symbol)} />
          ))
        )}
      </div>
    </div>
  );
}

export default function SectorLeaders() {
  const { data, error, loading } = useResource<Payload>('/api/sector-leaders');
  const [drawerSymbol, setDrawerSymbol] = useState<string | null>(null);

  if (loading) {
    return <div className="text-sm text-zinc-500 py-8 text-center">Loading sector leaders…</div>;
  }
  if (error || !data) {
    return (
      <div className="rounded border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
        {error ?? 'sector-leaders unavailable'}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-4 py-3">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-base font-semibold text-white">Sector Leaders</div>
            <div className="text-[11px] text-zinc-400 mt-0.5">
              Top {data.topN} by composite within each peer group. Q ≥ 0 gated, same as the main Alpha Basket.
              Different lens — surfaces the best name in each flavor rather than concentrating on whatever sector dominates the global stack.
            </div>
          </div>
          {data.asOf && (
            <div className="text-[10px] font-mono text-zinc-500">scored {new Date(data.asOf).toLocaleString()}</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.order.map((pg) => (
          <SectorCard
            key={pg}
            label={data.labels[pg]}
            blurb={PEER_GROUP_BLURB[pg]}
            rows={data.leaders[pg] ?? []}
            onPickSymbol={setDrawerSymbol}
          />
        ))}
      </div>

      {drawerSymbol && (
        <StockStoryDrawer
          symbol={drawerSymbol}
          onClose={() => setDrawerSymbol(null)}
        />
      )}
    </div>
  );
}
